import * as React from 'react';
import { Check, Clock, Phone, User, Warehouse, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { AddressComplementPart } from '@klareospace/pharos-lib-front-utils';
import { extractAddressComplementParts, formatTimeWindow } from '@klareospace/pharos-lib-front-utils';
import { countryFromDialCode, formatNationalNumber } from '@klareospace/pharos-lib-front-utils/utils';
import { type Contact, type Delivery, type Pickup, type Task, type TimeSlot } from '@klareospace/pharos-lib-types';
import {
  findDeliveryJob,
  findPickupJob,
  formatDeliveryAddress,
  formatPostalAddress,
  formatRequestedAddress,
  isDefined,
  resolveDeliveryPostalAddress,
} from '@klareospace/pharos-lib-utils';
import {
  cn,
  DRAWER_FIELD_LABEL_CLASSES,
  IconText,
  STATUS_BADGE_COLORS,
  StatusBadge,
} from '@klareospace/pharos-lib-web-utils';

import { AddressComplementParts } from '&/features/orders/components';
import { resolveDeliveryIcon, resolvePickupIcon } from '&/features/orders/utils';

import { resolveDeliveryStepState, resolvePickupStepState, type TaskTimelineStepState } from '../utils/task-timeline';
import { TaskDetailSection } from './task-detail-section';
import { TaskFailureCard } from './task-failure-card';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskPickupDeliveryTimelineProps {
  readonly task: Task;
}

interface LocationLines {
  /** Plain-address complement parts (digicode, elevator, floor, etc.) — empty for a site-backed line. */
  complementParts?: AddressComplementPart[];
  /** Icon for the primary line. Undefined for a plain address with no resolved site. */
  icon?: React.ElementType;
  /** Site color tint applied to the icon, when a site is resolved. */
  iconColor?: string;
  primary: string;
  secondary?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Border + ring color for the step marker's outer 26px circle, per step state. */
const STEP_MARKER_OUTER_CLASSES: Record<TaskTimelineStepState, string> = {
  done: 'border-success ring-success/40',
  failed: 'border-destructive ring-destructive/40',
  todo: 'border-border ring-border/50',
};

const STEP_CONNECTOR_CLASSES: Record<TaskTimelineStepState, string> = {
  done: 'bg-success',
  failed: 'bg-border',
  todo: 'bg-border',
};

const STEP_STATE_BADGE_STYLES: Record<TaskTimelineStepState, string> = {
  done: STATUS_BADGE_COLORS.success,
  failed: STATUS_BADGE_COLORS.danger,
  todo: STATUS_BADGE_COLORS.neutral,
};

const STEP_STATE_LABEL_KEYS: Record<TaskTimelineStepState, string> = {
  done: 'common:statuses.completed',
  failed: 'detail.failed',
  todo: 'common:statuses.todo',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Pickup always has a site — primary line is the site name (with its site-type icon and color),
 * secondary is its formatted address.
 */
function resolvePickupLocationLines(pickup: Pickup | undefined): LocationLines {
  if (pickup === undefined) return { primary: '—' };
  return {
    icon: resolvePickupIcon(pickup.site),
    iconColor: pickup.site.color,
    primary: pickup.site.name,
    secondary: formatPostalAddress(pickup.site.address),
  };
}

/** Delivery resolves to a site (with icon/color), or falls back to a plain address line with no icon. */
function resolveDeliveryLocationLines(delivery: Delivery | undefined): LocationLines {
  if (delivery?.site !== undefined) {
    return {
      icon: resolveDeliveryIcon(delivery.site),
      iconColor: delivery.site.color,
      primary: delivery.site.name,
      secondary: formatPostalAddress(delivery.site.address),
    };
  }
  const address = formatDeliveryAddress(delivery?.address) || formatRequestedAddress(delivery?.requestedAddress);
  const complementParts = extractAddressComplementParts(resolveDeliveryPostalAddress(delivery?.address));
  return { complementParts, primary: address || '—' };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface ContactLinesProps {
  readonly contact?: Contact | null;
}

function ContactLines({ contact }: ContactLinesProps): React.JSX.Element | null {
  if (contact == null) return null;

  return (
    <div className="mt-1 space-y-0.5">
      <IconText className="text-foreground" icon={User} size="xs">
        <span className="truncate">{contact.name}</span>
      </IconText>
      <IconText className="text-muted-foreground" icon={Phone} size="xs">
        <span>
          {contact.phone.dialCode}{' '}
          {formatNationalNumber(contact.phone.number, countryFromDialCode(contact.phone.dialCode))}
        </span>
      </IconText>
    </div>
  );
}

interface StepMarkerProps {
  readonly state: TaskTimelineStepState;
}

/** 26px circular step marker: Check when done, X when failed, or a small neutral dot for todo. */
function StepMarker({ state }: StepMarkerProps): React.JSX.Element {
  return (
    <div className="relative z-10 shrink-0">
      <div
        className={cn(
          'w-[26px] h-[26px] rounded-full bg-card border-4 shadow-sm ring-1 flex items-center justify-center',
          STEP_MARKER_OUTER_CLASSES[state],
        )}
      >
        {state === 'done' && <Check className="w-3 h-3 text-success" />}
        {state === 'failed' && <X className="w-3 h-3 text-destructive" />}
        {state === 'todo' && <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50" />}
      </div>
    </div>
  );
}

interface TimelineStepProps {
  readonly contact?: Contact | null;
  readonly isLast?: boolean;
  readonly lines: LocationLines;
  readonly stepLabel: string;
  readonly state: TaskTimelineStepState;
  readonly timeSlot?: TimeSlot;
}

/** Primary location line: site-type icon (tinted with the site color) when a site is resolved, plain text otherwise. */
function LocationPrimaryLine({ icon: Icon, iconColor, primary }: LocationLines): React.JSX.Element {
  if (Icon === undefined) {
    // No icon: set text size explicitly since it won't inherit IconText's implicit sizing.
    return <span className="text-sm font-medium text-foreground leading-tight mt-0.5 truncate">{primary}</span>;
  }
  return (
    <IconText icon={Icon} iconStyle={iconColor !== undefined ? { color: iconColor } : undefined} size="sm">
      <span className="font-medium text-foreground truncate">{primary}</span>
    </IconText>
  );
}

function TimelineStep({
  contact,
  isLast = false,
  lines,
  state,
  stepLabel,
  timeSlot,
}: TimelineStepProps): React.JSX.Element {
  const { t } = useTranslation('tasks');

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <StepMarker state={state} />
        {!isLast && <div className={cn('w-px flex-1 my-1', STEP_CONNECTOR_CLASSES[state])} />}
      </div>
      <div className={cn('min-w-0 flex-1', !isLast && 'pb-4')}>
        <div className="flex items-center justify-between gap-2">
          <p className={DRAWER_FIELD_LABEL_CLASSES}>{stepLabel}</p>
          <StatusBadge label={t(STEP_STATE_LABEL_KEYS[state])} status={state} styles={STEP_STATE_BADGE_STYLES} />
        </div>
        <LocationPrimaryLine {...lines} />
        {lines.secondary !== undefined && lines.secondary !== '' && (
          <span className="text-xs text-foreground block">{lines.secondary}</span>
        )}
        <AddressComplementParts parts={lines.complementParts ?? []} />
        <ContactLines contact={contact} />
        <div className="mt-1">
          <IconText icon={Clock} size="xs">
            <span>{isDefined(timeSlot) ? formatTimeWindow(timeSlot) : t('noTimeslot')}</span>
          </IconText>
        </div>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Vertical 2-step Pickup → Delivery timeline shown in the Task Detail popin. Always the first section, no top divider. */
export function TaskPickupDeliveryTimeline({ task }: TaskPickupDeliveryTimelineProps): React.JSX.Element {
  const { t } = useTranslation('tasks');

  const pickup = findPickupJob(task)?.data;
  const delivery = findDeliveryJob(task)?.data;

  return (
    <TaskDetailSection icon={Warehouse} title={t('detail.pickupDelivery')} withTopDivider={false}>
      <TimelineStep
        contact={pickup?.contact}
        lines={resolvePickupLocationLines(pickup)}
        state={resolvePickupStepState(task)}
        stepLabel={t('orders:drawer.pickup')}
        timeSlot={pickup?.timeSlot}
      />
      <TimelineStep
        contact={delivery?.contact}
        isLast
        lines={resolveDeliveryLocationLines(delivery)}
        state={resolveDeliveryStepState(task)}
        stepLabel={t('common:fields.delivery')}
        timeSlot={delivery?.timeSlot}
      />
      <TaskFailureCard task={task} />
    </TaskDetailSection>
  );
}
